import { Injectable, NgZone } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ModalData } from '@models/helpers.model';
import { StatusLookup } from '@models/status.lookup.model';
import { Store } from '@ngxs/store';
import { OnMedModalComponent } from '@shared/components/onmed-modal/onmed-modal.component';
import { AppSelectors } from '@store/app/app.selectors';
import { SignOutCallback } from '@store/auth/auth.actions';
import { AuthSelectors } from '@store/auth/auth.selectors';
import { ChangeOnlineStatus } from '@store/employees/employee.actions';
import { Subject, fromEvent, merge, timer } from 'rxjs';
import { filter, switchMap, takeWhile, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class IdleTimeoutService {
  private idleTimeout = 3600000; // 1 hour (in milliseconds)
  private warningTime = 300000; // 5 minutes before logout
  private idleTime = 1200000; // 5 minutes for idle detection
  private userActivity$ = new Subject<void>();
  private warningShown = false;
  private logoutTimer: any;
  private idleTimer: any; // Timer for detecting idle state
  private isIdle = false;
  private audioContext: AudioContext | null = null;

  constructor(private store: Store, private ngZone: NgZone, private dialog: MatDialog) { }

  startWatching() {
    this.ngZone.runOutsideAngular(() => {
      const activityEvents = merge(
        fromEvent(document, 'mousemove'),
        fromEvent(document, 'keydown'),
        fromEvent(document, 'click'),
        fromEvent(document, 'scroll')
      );

      activityEvents.subscribe(() => {
        this.initializeAudioContext(); // Ensures audio can play later
        this.userActivity$.next();
        this.warningShown = false;
        clearTimeout(this.idleTimer);
        clearTimeout(this.logoutTimer);
        this.startIdleTimer(); // Restart idle timer on activity
        if(this.isIdle) {
          this.isIdle = false;
          this.setUserStatus('Available');
        }
      });

      this.store.select(AuthSelectors.isLoggedIn).pipe(
        switchMap(isLoggedIn => 
          isLoggedIn
            ? this.userActivity$.pipe(
                switchMap(() => {
                  this.warningShown = false;
                  return timer(this.idleTimeout - this.warningTime);
                }),
                tap(() => this.showWarningNotification()),
                takeWhile(() => isLoggedIn) // Stop when user logs out
              )
            : []
        )
      ).subscribe();
      this.startIdleTimer(); // Start the idle timer initially
    });
  }

  private startIdleTimer() {
    this.idleTimer = setTimeout(() => {
      this.isIdle = true;
      this.setUserStatus('Away'); // Change user status to "idle"
    }, this.idleTime);
  }

  private setUserStatus(status: string) {
    const awayStatus = this.store.selectSnapshot(AppSelectors.status(new StatusLookup(status, 'Employee Availability')))[0];
    const loggedInUser = this.store.selectSnapshot(AuthSelectors.loggedInUser);
    if(loggedInUser)
      this.store.dispatch(new ChangeOnlineStatus(loggedInUser.employees[0].id, awayStatus.id, false, awayStatus )); // Example action
  }

  private initializeAudioContext() {
    if (!this.audioContext) {
      this.audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
  }

  private showWarningNotification() {
    if (this.warningShown) return;
    this.warningShown = true;

    this.ngZone.run(() => {
      this.playWarningSound();

      const modalData = new ModalData({
        title: 'OnMed',
        text: 'There was no activity for 60 minutes. The user will be logged out.',
        rightBtnTxt: 'Ok',
        leftBtnTxt: ''
      });

      const modal = this.dialog.open(OnMedModalComponent, {
        width: '327px',
        height: 'auto',
        disableClose: false,
        data: modalData
      });

      modal.componentInstance.onClose.subscribe(() => {
        this.warningShown = false;
        this.userActivity$.next();
        clearTimeout(this.logoutTimer);
      })

      modal.componentInstance.onRightAction.subscribe(() => {
        modal.close();
        this.warningShown = false;
        this.userActivity$.next();
        clearTimeout(this.logoutTimer);
      });

      modal.afterClosed().subscribe(() => {
        modal.componentInstance.onRightAction.unsubscribe();
      });

      this.startLogoutTimer();
    });
  }

  private startLogoutTimer() {
    this.logoutTimer = setTimeout(() => {
      this.store.dispatch(new SignOutCallback());
    }, this.warningTime);
  }

  private playWarningSound() {
    if (!this.audioContext) return;
    const oscillator = this.audioContext.createOscillator();
    const gainNode = this.audioContext.createGain();

    oscillator.type = 'sine'; // Type of beep sound
    oscillator.frequency.value = 440; // 1000 Hz = beep sound
    gainNode.gain.setValueAtTime(0.1, this.audioContext.currentTime); // Reduce volume

    oscillator.connect(gainNode);
    gainNode.connect(this.audioContext.destination);

    oscillator.start();
    oscillator.stop(this.audioContext.currentTime + 0.5); // Play for 0.5 seconds
  }
}
